import {
  getCollaboratorPortalOrigin,
  listBackofficeCollaboratorAccess,
  loadCurrentCollaboratorAccess,
  mapCollaboratorInvitationRow,
  mapCollaboratorProfileRow,
} from "./collaboratorAccessService";
import { supabase } from "./client";
import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { withRequestDeadline } from "./requestDeadline";

const INVOICE_BUCKET = "collaborator-invoices";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeText = (value, maxLength = 180) =>
  String(value || "").trim().slice(0, maxLength);

const normalizeEmail = (value) => normalizeText(value, 180).toLowerCase();

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const toDate = (value) => normalizeText(value, 40).slice(0, 10);

const normalizeError = (error, fallbackMessage) => {
  const message = normalizeText(error?.message, 700);
  const details = normalizeText(error?.details, 700);

  if (
    error?.code === "42P01" ||
    error?.code === "PGRST202" ||
    /collaborator_(locations|settlements|program_settings)/i.test(message)
  ) {
    return new Error(
      "La operacion de colaboradores todavia no esta instalada en Supabase. Aplica la migracion operativa antes de continuar."
    );
  }

  return new Error(message || details || fallbackMessage);
};

const formatCycleLabel = (cycleStart) => {
  const value = toDate(cycleStart);
  if (!value) {
    return "Ciclo mensual";
  }

  const date = new Date(`${value}T12:00:00`);
  const label = new Intl.DateTimeFormat("es-AR", {
    month: "long",
    year: "numeric",
  }).format(date);
  return label.slice(0, 1).toUpperCase() + label.slice(1);
};

export const mapCollaboratorProgramSettingsRow = (row = {}) => ({
  minimumCommissionableSales: toMoney(
    row.minimum_commissionable_sales ?? 1500000
  ),
  programCommissionRate: Number(row.program_commission_rate || 0.006),
  statementIssueDay: Number(row.statement_issue_day || 5),
  invoiceDueDay: Number(row.invoice_due_day || 10),
  paymentDueDay: Number(row.payment_due_day || 15),
  restaurantSellerRate: Number(row.restaurant_seller_rate || 0.002),
  branchSellerRate: Number(row.branch_seller_rate || 0.001),
  parentStarTemporaryRate: Number(row.parent_star_temporary_rate || 0.003),
  companyRestaurantRate: Number(row.company_restaurant_rate || 0.004),
  companyTemporaryRate: Number(row.company_temporary_rate || 0.001),
  companyBranchRate: Number(row.company_branch_rate || 0.005),
  normalPromotionLocationTarget: Number(
    row.normal_promotion_location_target || 30
  ),
  starNetworkLocationTarget: Number(row.star_network_location_target || 100),
});

export const mapCollaboratorLocationRow = (row, origin = "") => {
  if (!row) {
    return null;
  }

  const portalOrigin = getCollaboratorPortalOrigin(origin);
  const restaurantEmail = normalizeEmail(row.restaurant_email);
  const sellerCode = normalizeText(row.owner_seller_code, 40);
  const referralToken = normalizeText(row.referral_token, 80);

  return {
    id: row.id,
    ownerProfileId: row.owner_profile_id,
    networkStarProfileId: row.network_star_profile_id || "",
    companyId: row.company_id || "",
    branchId: row.branch_id || "",
    companyName: normalizeText(row.company_name, 120),
    branchName: normalizeText(row.branch_name, 120),
    restaurantEmail,
    businessType: normalizeText(row.business_type, 80) || "Restaurante",
    locationType: row.location_type === "branch" ? "branch" : "restaurant",
    commissionableSales: toMoney(row.current_commissionable_sales),
    collectedPlatformFee: toMoney(row.current_collected_platform_fee),
    collaboratorRate: Number(row.collaborator_rate || 0),
    collaboratorCommission: toMoney(row.current_collaborator_commission),
    status: normalizeText(row.status, 40) || "prelinked",
    referralToken,
    linkedAt: toDate(row.linked_at || row.created_at),
    claimedAt: row.claimed_at || "",
    currentCycleStart: toDate(row.current_cycle_start),
    onboardingUrl:
      portalOrigin && referralToken
        ? `${portalOrigin}/login?register=1&email=${encodeURIComponent(
            restaurantEmail
          )}&seller=${encodeURIComponent(sellerCode)}&ref=${encodeURIComponent(
            referralToken
          )}`
        : "",
  };
};

export const mapCollaboratorSettlementRow = (row) => {
  if (!row) {
    return null;
  }

  return {
    id: row.id,
    profileId: row.profile_id,
    cycleLabel: formatCycleLabel(row.cycle_start),
    cycleStart: toDate(row.cycle_start),
    cycleEnd: toDate(row.cycle_end),
    statementDate: toDate(row.statement_date),
    invoiceDueDate: toDate(row.invoice_due_date),
    paymentDueDate: toDate(row.payment_due_date),
    commissionableSales: toMoney(row.commissionable_sales),
    directCommission: toMoney(row.direct_commission),
    starTemporaryCommission: toMoney(row.star_temporary_commission),
    networkBonus: toMoney(row.network_bonus),
    creditNotes: toMoney(row.credit_notes),
    netAmount: toMoney(row.net_amount),
    networkLocationCount: Number(row.network_location_count || 0),
    networkBonusRate: Number(row.network_bonus_rate || 0),
    status: normalizeText(row.status, 40) || "awaiting_invoice",
    invoiceNumber: normalizeText(row.invoice_number, 80),
    invoiceFilePath: normalizeText(row.invoice_file_path, 500),
    invoiceFileName: normalizeText(row.invoice_file_name, 180),
    invoiceSubmittedAt: row.invoice_submitted_at || "",
    invoiceApprovedAt: row.invoice_approved_at || "",
    paymentScheduledForNextCycle:
      row.payment_scheduled_for_next_cycle === true,
    paidAt: row.paid_at || "",
    paymentReference: normalizeText(row.payment_reference, 120),
  };
};

const isOperationalLocation = (location, settings) =>
  location.status !== "cancelled" &&
  toMoney(location.commissionableSales) >=
    toMoney(settings.minimumCommissionableSales) &&
  toMoney(location.collectedPlatformFee) > 0;

const buildNetworkState = ({ profiles, locations, starStatusRows, settings }) => {
  const networkMembers = profiles
    .filter((profile) => profile.invitedByProfileId)
    .map((profile) => {
      const attributedLocations = locations.filter(
        (location) =>
          location.ownerProfileId === profile.id &&
          location.networkStarProfileId === profile.invitedByProfileId
      );

      return {
        id: `network-member-${profile.id}`,
        starProfileId: profile.invitedByProfileId,
        profileId: profile.id,
        name: `${profile.firstName} ${profile.lastName}`.trim(),
        email: profile.email,
        sellerCode: profile.sellerCode,
        activeLocationCount: attributedLocations.filter((location) =>
          isOperationalLocation(location, settings)
        ).length,
        status: profile.status,
      };
    });

  const starNetworkStatus = Object.fromEntries(
    (starStatusRows || []).map((row) => [
      row.star_profile_id,
      {
        previouslyActivated: row.previously_activated === true,
        lockedUntilTarget: row.locked_until_target === true,
        currentOperationalLocationCount: Number(
          row.current_operational_location_count || 0
        ),
        currentBonusRate: Number(row.current_bonus_rate || 0),
        lastCycleStart: toDate(row.last_cycle_start),
      },
    ])
  );

  return {
    networkMembers,
    networkPortfolios: [],
    starNetworkStatus,
  };
};

const loadProgramRows = async ({ backoffice = false, origin = "" } = {}) => {
  ensureSupabaseReady();

  const access = backoffice
    ? await listBackofficeCollaboratorAccess({ origin })
    : await loadCurrentCollaboratorAccess({ origin });
  const hasLinkedProfile = access.profiles.some((profile) => profile.userId);

  if (!backoffice && !hasLinkedProfile) {
    return {
      access,
      settingsRow: {},
      locationRows: [],
      settlementRows: [],
      starStatusRows: [],
    };
  }

  let refreshWarning = "";
  try {
    const refreshResult = await withRequestDeadline(
      (signal) => supabase.rpc(
        "refresh_collaborator_program_metrics",
        { p_include_settlements: backoffice === true }
      ).abortSignal(signal),
      { timeoutMs: backoffice ? 12000 : 4000 }
    );
    if (refreshResult.error) {
      throw normalizeError(refreshResult.error, "No pudimos actualizar las metricas del programa.");
    }
  } catch (error) {
    if (backoffice) throw error;
    refreshWarning = "No pudimos actualizar los importes. Se muestran los ultimos datos guardados.";
  }

  const [
    settingsResult,
    locationsResult,
    settlementsResult,
    starStatusResult,
  ] = await withRequestDeadline((signal) => Promise.all([
    supabase
      .from("collaborator_program_settings")
      .select("*")
      .eq("singleton_key", "default")
      .single().abortSignal(signal),
    supabase
      .from("collaborator_locations")
      .select("*, owner:collaborator_profiles!owner_profile_id(seller_code)")
      .order("created_at", { ascending: false }).abortSignal(signal),
    supabase
      .from("collaborator_settlements")
      .select("*")
      .order("cycle_start", { ascending: false }).abortSignal(signal),
    supabase.from("collaborator_star_network_status").select("*").abortSignal(signal),
  ]), { message: "No pudimos cargar el panel a tiempo. Vuelve a intentar." });

  const firstError = [
    settingsResult.error,
    locationsResult.error,
    settlementsResult.error,
    starStatusResult.error,
  ].find(Boolean);
  if (firstError) {
    throw normalizeError(
      firstError,
      "No pudimos cargar la operacion del programa de colaboradores."
    );
  }

  return {
    access,
    settingsRow: settingsResult.data,
    locationRows: locationsResult.data || [],
    settlementRows: settlementsResult.data || [],
    starStatusRows: starStatusResult.data || [],
    refreshWarning,
  };
};

export const loadCollaboratorProgramState = async ({
  backoffice = false,
  origin = typeof window === "undefined" ? "" : window.location.origin,
} = {}) => {
  const rows = await loadProgramRows({ backoffice, origin });
  const settings = mapCollaboratorProgramSettingsRow(rows.settingsRow);
  const profiles = rows.access.profiles || [];
  const locations = rows.locationRows
    .map((row) =>
      mapCollaboratorLocationRow(
        {
          ...row,
          owner_seller_code: row.owner?.seller_code,
        },
        origin
      )
    )
    .filter(Boolean);
  const networkState = buildNetworkState({
    profiles,
    locations,
    starStatusRows: rows.starStatusRows,
    settings,
  });

  return {
    version: 2,
    programSettings: settings,
    profiles,
    locations,
    ...networkState,
    settlements: rows.settlementRows
      .map(mapCollaboratorSettlementRow)
      .filter(Boolean),
    invitations: rows.access.invitations || [],
    refreshWarning: rows.refreshWarning || "",
    updatedAt: new Date().toISOString(),
  };
};

export const createCollaboratorLocationReferral = async (
  values = {},
  origin = typeof window === "undefined" ? "" : window.location.origin
) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc(
    "create_collaborator_location_referral",
    {
      p_company_name: normalizeText(values.companyName, 120),
      p_branch_name: normalizeText(values.branchName, 120),
      p_restaurant_email: normalizeEmail(values.restaurantEmail),
      p_business_type: normalizeText(values.businessType, 80),
      p_location_type: values.locationType === "branch" ? "branch" : "restaurant",
    }
  );

  if (error) {
    throw normalizeError(error, "No pudimos vincular el comercio.");
  }

  return mapCollaboratorLocationRow(
    {
      ...data,
      owner_seller_code: normalizeText(values.sellerCode, 40),
    },
    origin
  );
};

export const updateCollaboratorLocationStatus = async (
  locationId,
  status
) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "update_collaborator_location_status",
    {
      p_location_id: locationId,
      p_status: status,
    }
  );
  if (error) {
    throw normalizeError(error, "No pudimos actualizar la sucursal.");
  }
  return mapCollaboratorLocationRow(data);
};

export const updateCollaboratorProgramSettings = async (values = {}) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "update_collaborator_program_settings",
    {
      p_minimum_commissionable_sales: toMoney(
        values.minimumCommissionableSales
      ),
      p_statement_issue_day: Number(values.statementIssueDay),
      p_invoice_due_day: Number(values.invoiceDueDay),
      p_payment_due_day: Number(values.paymentDueDay),
    }
  );
  if (error) {
    throw normalizeError(error, "No pudimos actualizar las reglas.");
  }
  return mapCollaboratorProgramSettingsRow(data);
};

export const updateCollaboratorProfileStatus = async (profileId, status) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "update_collaborator_profile_status",
    { p_profile_id: profileId, p_status: status }
  );
  if (error) {
    throw normalizeError(error, "No pudimos actualizar el colaborador.");
  }
  return mapCollaboratorProfileRow(data);
};

export const promoteCollaboratorToStar = async (profileId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "promote_collaborator_to_star",
    { p_profile_id: profileId }
  );
  if (error) {
    throw normalizeError(error, "No pudimos promover al colaborador.");
  }
  return mapCollaboratorProfileRow(data);
};

export const updateCollaboratorAccessEmail = async ({
  profileId,
  invitationId,
  email,
  origin = typeof window === "undefined" ? "" : window.location.origin,
}) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "update_collaborator_access_email",
    {
      p_profile_id: profileId,
      p_invitation_id: invitationId,
      p_email: normalizeEmail(email),
    }
  );
  if (error) {
    throw normalizeError(error, "No pudimos corregir el correo de acceso.");
  }
  return {
    profile: mapCollaboratorProfileRow(data?.profile),
    invitation: mapCollaboratorInvitationRow(data?.invitation, origin),
  };
};

const safeInvoiceFileName = (value) =>
  normalizeText(value, 160)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "factura";

export const registerCollaboratorSettlementInvoice = async ({
  profileId,
  settlementId,
  invoiceNumber,
  file,
}) => {
  ensureSupabaseReady();
  if (!file) {
    throw new Error("Selecciona el archivo PDF o imagen de la factura.");
  }
  if (Number(file.size || 0) > 10 * 1024 * 1024) {
    throw new Error("La factura no puede superar los 10 MB.");
  }

  const fileName = safeInvoiceFileName(file.name);
  const filePath = `${profileId}/${settlementId}/${Date.now()}-${fileName}`;
  const upload = await supabase.storage
    .from(INVOICE_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      contentType: file.type || undefined,
      upsert: false,
    });

  if (upload.error) {
    throw normalizeError(upload.error, "No pudimos subir la factura.");
  }

  const { data, error } = await supabase.rpc(
    "register_collaborator_settlement_invoice",
    {
      p_settlement_id: settlementId,
      p_invoice_number: normalizeText(invoiceNumber, 80),
      p_invoice_file_path: upload.data.path,
      p_invoice_file_name: normalizeText(file.name, 180),
    }
  );

  if (error) {
    await supabase.storage.from(INVOICE_BUCKET).remove([upload.data.path]);
    throw normalizeError(error, "No pudimos registrar la factura.");
  }

  return mapCollaboratorSettlementRow(data);
};

export const createCollaboratorInvoiceSignedUrl = async (filePath) => {
  ensureSupabaseReady();
  const normalizedPath = normalizeText(filePath, 500);
  if (!normalizedPath) {
    throw new Error("La liquidacion no tiene una factura adjunta.");
  }

  const { data, error } = await supabase.storage
    .from(INVOICE_BUCKET)
    .createSignedUrl(normalizedPath, 10 * 60);

  if (error || !data?.signedUrl) {
    throw normalizeError(error, "No pudimos abrir la factura.");
  }

  return data.signedUrl;
};

export const approveCollaboratorSettlementInvoice = async (settlementId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "approve_collaborator_settlement_invoice",
    { p_settlement_id: settlementId }
  );
  if (error) {
    throw normalizeError(error, "No pudimos aprobar la factura.");
  }
  return mapCollaboratorSettlementRow(data);
};

export const markCollaboratorSettlementPaid = async (
  settlementId,
  paymentReference
) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "mark_collaborator_settlement_paid",
    {
      p_settlement_id: settlementId,
      p_payment_reference: normalizeText(paymentReference, 120),
    }
  );
  if (error) {
    throw normalizeError(error, "No pudimos registrar la transferencia.");
  }
  return mapCollaboratorSettlementRow(data);
};
